import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useLocation, useOutletContext } from 'react-router-dom';
import { COMMAND_STATUS_TONES, iotColor, iotFont, iotLayout, iotMixin, iotRadius, requesterBadge } from './iot_console_theme';

// PENDING(발행 전 1초 미만)·PUBLISHED(ACK 대기 최대 10초)는 곧 종결 상태로 바뀌는 과도 상태라 필터에서 뺀다.
// 행에 잠깐 보일 수는 있으므로 톤(COMMAND_STATUS_TONES)은 그대로 둔다.
const STATUSES = ['ALL', 'SUCCEEDED', 'FAILED', 'TIMED_OUT', 'REJECTED'];
const STATE_FIELDS = Object.freeze([['power', '전원'], ['mode', '모드'], ['fan', '풍량'], ['temp', '온도']]);
const KST_SECONDS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
});

function initialDeviceId(location) {
    return new URLSearchParams(location?.search || '').get('device_id') || '';
}

// KST `MM.DD HH:mm:ss` — 파싱 실패 시 원문, 값이 없으면 "-".
function requestedAt(value) {
    if (!value) return '-';
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) return String(value);
    const parts = Object.fromEntries(
        KST_SECONDS_FORMATTER.formatToParts(parsed)
            .filter((part) => part.type !== 'literal')
            .map((part) => [part.type, part.value])
    );
    return `${parts.month}.${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

function stateValue(key, value) {
    if (value === null || value === undefined || value === '') return '—';
    return key === 'temp' ? `${value}℃` : String(value);
}

function IotControlLogs() {
    const { role } = useOutletContext() || {};
    const location = useLocation();
    const canView = role === 'admin' || role === 'staff';
    const [deviceId, setDeviceId] = useState(() => initialDeviceId(location));
    const [status, setStatus] = useState('ALL');
    const [logs, setLogs] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(canView);
    const [error, setError] = useState('');

    const load = async ({ append = false, nextCursor = null } = {}) => {
        setLoading(true); setError('');
        try {
            const params = {};
            if (deviceId.trim()) params.device_id = deviceId.trim();
            if (status !== 'ALL') params.status = status;
            if (nextCursor) params.cursor = nextCursor;
            const response = await axios.get('/api/admin/iot/ir-control-logs', { params });
            setLogs((current) => append ? [...current, ...(response.data.logs || [])] : (response.data.logs || []));
            setCursor(response.data.next_cursor || null);
        } catch (loadError) {
            setError(loadError.response?.data?.message || 'IR 제어 로그를 불러오지 못했습니다.');
        } finally { setLoading(false); }
    };

    useEffect(() => { if (canView) load(); /* 최초 진입과 조회 조건 변경 때만 새 목록을 읽는다. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canView, status]);

    if (!canView) {
        return (
            <PageShell>
                <StatePanel role="alert">IoT 모니터링 권한이 필요합니다.</StatePanel>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <Content>
                <Header>
                    <h1>IR 제어 로그</h1>
                    <p>요청·MQTT 전달·모듈의 IR 송신 ACK 기록입니다. ACK 성공은 실제 에어컨 상태 확인이 아닙니다.</p>
                </Header>

                <FilterCard onSubmit={(event) => { event.preventDefault(); load(); }}>
                    <SearchField>
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                            <circle cx="7" cy="7" r="4.6" />
                            <path d="M10.5 10.5 14 14" />
                        </svg>
                        <input aria-label="장치 ID" value={deviceId} onChange={(event) => setDeviceId(event.target.value)} placeholder="device_id" />
                    </SearchField>
                    <ChipGroup role="group" aria-label="명령 상태">
                        {STATUSES.map((item) => (
                            <StatusFilterChip
                                key={item}
                                type="button"
                                aria-pressed={status === item}
                                $active={status === item}
                                onClick={() => setStatus(item)}
                            >
                                {item}
                            </StatusFilterChip>
                        ))}
                    </ChipGroup>
                    <SubmitButton type="submit" disabled={loading}>조회</SubmitButton>
                </FilterCard>

                {error && <Message role="alert">{error}</Message>}

                <TableCard>
                    {loading && logs.length === 0 ? (
                        <EmptyState>IR 제어 로그를 불러오는 중입니다.</EmptyState>
                    ) : logs.length === 0 ? (
                        <EmptyState>조회된 IR 제어 로그가 없습니다.</EmptyState>
                    ) : (
                        <TableScroller>
                            <table>
                                <colgroup>
                                    <col style={{ width: '168px' }} />
                                    <col style={{ width: '118px' }} />
                                    <col />
                                    <col style={{ width: '168px' }} />
                                    <col style={{ width: '132px' }} />
                                    <col style={{ width: '140px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th scope="col">요청 시각</th>
                                        <th scope="col">장치</th>
                                        <th scope="col">요청 상태</th>
                                        <th scope="col">요청자</th>
                                        <th scope="col">명령 상태</th>
                                        <th scope="col">오류</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => {
                                        const badge = requesterBadge(log.requester?.role);
                                        const tone = COMMAND_STATUS_TONES[log.status] || COMMAND_STATUS_TONES.PENDING;
                                        const requester = `${log.requester?.role || '-'} · ${log.requester?.user_id || '-'}`;
                                        return (
                                            <tr key={log.command_id}>
                                                <TimeCell>{requestedAt(log.created_at)}</TimeCell>
                                                {/* 시안 열 폭(118·168·140px)을 넘는 값은 말줄임하고 title로 전체 값을 보여준다. */}
                                                <DeviceCell title={log.device_id}>{log.device_id}</DeviceCell>
                                                <td>
                                                    <StatePills>
                                                        {STATE_FIELDS.map(([key, label]) => (
                                                            <StatePill key={key}><b>{label}</b>{stateValue(key, log.state?.[key])}</StatePill>
                                                        ))}
                                                    </StatePills>
                                                </td>
                                                <td>
                                                    <Requester title={requester}>
                                                        <RoleBadge $badge={badge} aria-hidden="true">{badge.short}</RoleBadge>
                                                        <span>{requester}</span>
                                                    </Requester>
                                                </td>
                                                <td>
                                                    <CommandStatus $tone={tone}><i aria-hidden="true" />{log.status}</CommandStatus>
                                                </td>
                                                <ErrorCell $hasError={Boolean(log.error_code)} title={log.error_code || undefined}>{log.error_code || '-'}</ErrorCell>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </TableScroller>
                    )}
                    {cursor && (
                        <MoreRow>
                            <MoreButton type="button" disabled={loading} onClick={() => load({ append: true, nextCursor: cursor })}>더 보기</MoreButton>
                        </MoreRow>
                    )}
                </TableCard>
            </Content>
        </PageShell>
    );
}

export default IotControlLogs;

// 시안 04 열 폭 합(168+118+280+168+132+140) — 좁은 화면은 카드 안에서 가로 스크롤한다.
const TABLE_MIN_WIDTH = '1006px';

const PageShell = styled.main`
    min-height: calc(100vh - 130px);
    padding: ${iotLayout.pagePadding};
    background: ${iotColor.page};
    color: ${iotColor.text};

    @media (max-width: 720px) {
        padding: ${iotLayout.pagePaddingSmall};
    }
`;

const Content = styled.div`
    max-width: ${iotLayout.maxWidth};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: ${iotLayout.gap};
`;

const Header = styled.header`
    h1 {
        margin: 0;
        color: ${iotColor.ink};
        font-size: 24px;
        font-weight: 800;
        line-height: 1.25;
        letter-spacing: -0.02em;
    }

    p {
        max-width: 720px;
        margin: 6px 0 0;
        color: ${iotColor.textMuted};
        font-size: 13px;
        line-height: 1.5;
    }
`;

const FilterCard = styled.form`
    ${iotMixin.card};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 16px;
`;

const SearchField = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    width: 260px;
    max-width: 100%;

    svg {
        position: absolute;
        left: 12px;
        stroke: ${iotColor.textMuted};
        pointer-events: none;
    }

    input {
        ${iotMixin.control};
        ${iotMixin.focusRing};
        ${iotFont.mono};
        width: 100%;
        padding: 0 12px 0 34px;
        color: ${iotColor.ink};
    }
`;

const ChipGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const StatusFilterChip = styled.button`
    ${iotMixin.focusRing};
    height: 38px;
    padding: 0 13px;
    border: 1px solid ${({ $active }) => $active ? iotColor.chipActive : iotColor.controlLine};
    border-radius: ${iotRadius.control};
    background: ${({ $active }) => $active ? iotColor.chipActive : iotColor.surface};
    color: ${({ $active }) => $active ? iotColor.onDark : iotColor.textSub};
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
`;

const SubmitButton = styled.button`
    ${iotMixin.focusRing};
    margin-left: auto;
    height: 38px;
    padding: 0 20px;
    border: 1px solid ${iotColor.accent};
    border-radius: ${iotRadius.control};
    background: ${iotColor.accent};
    color: ${iotColor.onDark};
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${iotColor.accentHover};
        background: ${iotColor.accentHover};
    }

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const Message = styled.div`
    padding: 12px 16px;
    border: 1px solid ${COMMAND_STATUS_TONES.FAILED.border};
    border-radius: ${iotRadius.control};
    background: ${COMMAND_STATUS_TONES.FAILED.bg};
    color: ${COMMAND_STATUS_TONES.FAILED.fg};
    font-size: 13px;
    font-weight: 600;
`;

const TableCard = styled.section`
    ${iotMixin.card};
    overflow: hidden;
`;

const TableScroller = styled.div`
    overflow-x: auto;

    table {
        width: 100%;
        min-width: ${TABLE_MIN_WIDTH};
        table-layout: fixed;
        border-collapse: collapse;
    }

    th {
        padding: 10px 16px;
        border-bottom: 1px solid ${iotColor.cardLine};
        background: ${iotColor.headerRow};
        color: ${iotColor.textMuted};
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-align: left;
        white-space: nowrap;
    }

    td {
        height: 54px;
        padding: 0 16px;
        border-bottom: 1px solid ${iotColor.rowLine};
        vertical-align: middle;
    }

    tbody tr:hover {
        background: ${iotColor.rowHover};
    }
`;

const TimeCell = styled.td`
    color: ${iotColor.textSub};
    font-size: 12.5px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const DeviceCell = styled.td`
    ${iotFont.mono};
    color: ${iotColor.ink};
    font-size: 12px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StatePills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 0;
`;

const StatePill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border: 1px solid ${iotColor.cardLine};
    border-radius: ${iotRadius.tag};
    background: ${iotColor.headerRow};
    color: ${iotColor.textSub};
    font-size: 11.5px;
    font-weight: 700;
    white-space: nowrap;

    b {
        color: ${iotColor.textMuted};
        font-weight: 600;
    }
`;

const Requester = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;

    > span:last-child {
        overflow: hidden;
        color: ${iotColor.textSub};
        font-size: 12.5px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const RoleBadge = styled.span`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: ${({ $badge }) => $badge.bg};
    color: ${({ $badge }) => $badge.fg};
    font-size: 10px;
    font-weight: 800;
`;

const CommandStatus = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border: 1px solid ${({ $tone }) => $tone.border};
    border-radius: ${iotRadius.pill};
    background: ${({ $tone }) => $tone.bg};
    color: ${({ $tone }) => $tone.fg};
    font-size: 11.5px;
    font-weight: 700;
    white-space: nowrap;

    i {
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${({ $tone }) => $tone.dot};
    }
`;

const ErrorCell = styled.td`
    ${iotFont.mono};
    color: ${({ $hasError }) => $hasError ? iotColor.errorText : iotColor.textMuted};
    font-size: 11.5px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EmptyState = styled.p`
    margin: 0;
    padding: 40px 18px;
    color: ${iotColor.textMuted};
    font-size: 13px;
    text-align: center;
`;

const MoreRow = styled.div`
    display: flex;
    justify-content: center;
    padding: 14px;
`;

const MoreButton = styled.button`
    ${iotMixin.control};
    ${iotMixin.focusRing};
    height: 36px;
    padding: 0 20px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${iotColor.rowHover};
    }

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const StatePanel = styled.div`
    ${iotMixin.card};
    max-width: ${iotLayout.maxWidth};
    margin: 0 auto;
    padding: 32px;
    color: ${iotColor.textMuted};
    font-size: 14px;
    text-align: center;
`;
